document.addEventListener('DOMContentLoaded', () => {
    const GS = window.GeolocationService;
    const cartDrawer = document.querySelector('cart-drawer');
    let sections = 'cart-icon-bubble,free-shipping-notice';

    if (cartDrawer) {
        const sectionId = cartDrawer.closest('.shopify-section')?.id;
        if (sectionId) {
            sections += `,${sectionId.replace('shopify-section-', '')}`;
        }
    }

    let storefrontAccessToken = theme.settings.themeStoreId;
    let shopifyGraphQlApiVersion = theme?.settings?.themeApiVersion;
    let shopifyGraphQlEndpoint = `https://${Shopify.shop}/api/${shopifyGraphQlApiVersion}/graphql.json`;

    let isUpdatingCart = false;
    let customerGeolocationDispatched = false;
    let locationData = getInitialLocationData();

    // If no valid data exists or it has expired, fetch new location data.
    if (!locationData || GS.isExpired(locationData.expiryTime)) {
        if (!GS.isBot()) {
            GS.fetchLocation().then(newData => {
                locationData = newData;
                updateCartAttributeData(locationData, false, true);
                initEventListeners();
            });
        }
    } else {
        updateCartAttributeData(locationData, false, true);
        initEventListeners();
    }

    document.addEventListener('geolocation:updated', event => {
        locationData = event.detail.userLocationData;
        updateCartAttributeData(locationData, false, true);
    });

    // Listen for customer geolocation events
    document.addEventListener('customer:geolocation:updated', (event) => {
        locationData = event.detail.userLocationData;
        updateCartAttributeData(locationData, false, true);
    });

    // Listen for precise geolocation events
    document.addEventListener('geolocation:preciseLocation', (event) => {
        locationData = event.detail.userLocationData;
        updateCartAttributeData(locationData, false, true);
    });

    /**
     * Retrieves initial location data. It first checks for Shopify customer data,
     * then falls back to localStorage via the GeolocationService.
     */
    function getInitialLocationData() {
        let data = null;

        if (window.shopifyCustomerData && window.shopifyCustomerData.defaultAddress) {
            data = GS.getCustomerLocationData(window.shopifyCustomerData.defaultAddress);
            GS.saveLocationData(data);

            // Dispatch once on page load so other components (energy-rebate,
            // quick-ship-geolocation, get-by-date-badge) receive the event.
            // Skip on subsequent calls from cart event handlers to prevent
            // duplicate updateCartAttributeData() cascades.
            if (!customerGeolocationDispatched) {
                customerGeolocationDispatched = true;
                document.dispatchEvent(new CustomEvent('customer:geolocation:updated', {
                    detail: { userLocationData: data }
                }));
            }
        }

        if (!data) {
            data = GS.getLocationData();
        }

        return data;
    }

    /**
     * Updates the cart attributes with the user location data.
     */
    function updateCartAttributeData(locationData, openDrawer = false, reloadDrawer = false) {
        if (!locationData || !locationData.region) return;
        if (isUpdatingCart) return;
        isUpdatingCart = true;

        const preparedLocationData = {
            city: locationData.city,
            region: locationData.region,
            zip: locationData.zip,
            precise: locationData.precise
        };

        const cartAttributes = {
            userGeolocation: JSON.stringify(preparedLocationData)
        };

        fetch(window.Shopify.routes.root + 'cart/update.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ attributes: cartAttributes, sections })
        })
        .then(async response => {
            const responseData = await response.json();
            if (!response.ok) {
                if (response.status === 422) {
                    await updateCartItems(responseData);
                }
            }
            return responseData;
        })
        .then(async data => {
            if (reloadDrawer) {
                if (cartDrawer && data?.status !== 422) {
                    cartDrawer.renderContents(data, openDrawer && (theme.settings.afterAtc === 'drawer'));
                }
            }
        })
        .catch(async error => {
            console.error(error.message || error.statusText || error);
        })
        .finally(() => {
            isUpdatingCart = false;
        });
    }

    /**
     * Sets up event listeners for cart and line item changes.
     */
    function initEventListeners() {
        document.addEventListener('on:cart:add', event => {
            const currentCartGeolocation = event.detail?.cart?.attributes?.userGeolocation
                ? JSON.parse(event.detail.cart.attributes.userGeolocation)
                : null;
            const latestLocationData = getInitialLocationData();
            if (shouldUpdateGeolocation(currentCartGeolocation, latestLocationData)) {
                updateCartAttributeData(latestLocationData, true, true);
            }
        });

        document.addEventListener('on:line-item:change', event => {
            const currentCartGeolocation = event.detail?.cart?.attributes?.userGeolocation
                ? JSON.parse(event.detail.cart.attributes.userGeolocation)
                : null;
            const latestLocationData = getInitialLocationData();
            if (shouldUpdateGeolocation(currentCartGeolocation, latestLocationData)) {
                updateCartAttributeData(latestLocationData, true, true);
            }
        });

        document.addEventListener('on:cart:error', event => {
            const currentCartGeolocation = event.detail?.cart?.attributes?.userGeolocation
                ? JSON.parse(event.detail.cart.attributes.userGeolocation)
                : null;
            const latestLocationData = getInitialLocationData();
            if (shouldUpdateGeolocation(currentCartGeolocation, latestLocationData)) {
                updateCartAttributeData(latestLocationData, false, false);
            }
        });
    }

    /**
     * Determines if the cart geolocation attribute should be updated.
     */
    function shouldUpdateGeolocation(currentCartGeolocation, newLocationData) {
        if (!newLocationData || !newLocationData.region) return false;
        if (!currentCartGeolocation) return true;
        return currentCartGeolocation.region !== newLocationData.region;
    }

    /**
     * Fetches the cart items that match the product IDs in the error message and removes them.
     * @param {Object} responseData - The response data containing the error message.
     * @returns {Promise<void>}
     */
    async function updateCartItems(responseData) {
        if (!responseData?.message) {
            console.log("No product found for removal from cart.");
            return;
        }

        const regex = /\(ID:\s*([A-Za-z]+)-(\d+)\)/g;
        const matches = [...responseData.message.matchAll(regex)];
        if (!matches.length) {
            console.log("No product IDs found for removal from cart.");
            return;
        }

        const productData = matches.map(match => ({
            messagesType: match[1],
            productId: match[2],
        }));

        try {
            const cartResponse = await fetch("/cart.js");
            if (!cartResponse.ok) {
                throw new Error(`Failed to retrieve cart, status: ${cartResponse.status}`);
            }

            const cart = await cartResponse.json();
            const productDataMap = new Map(
                productData.map(({ productId, messagesType }) => [productId, messagesType])
            );

            const cartItems = (cart.items || []).filter(item => {
                const idStr = item.product_id.toString();
                if (productDataMap.has(idStr)) {
                    item.messagesType = productDataMap.get(idStr);
                    return true;
                }
                return false;
            });

            let rawCartToken = cart?.token || "";
            if (rawCartToken.includes("?")) {
                rawCartToken = rawCartToken.split("?")[0];
            }
            const cartId = `gid://shopify/Cart/${rawCartToken}`;

            if (!cartItems.length) {
                console.log("No matching product IDs found in the cart.");
                return;
            }

            const customerProductData = new Map(
                productData.map(data => [`gid://shopify/Product/${data.productId}`, data.messagesType])
            );
            let customerCartItems = [];

            try {
                const customerCart = await getCustomerCartData(cartId);
                if (customerCart?.lines?.edges?.length) {
                    customerCartItems = customerCart.lines.edges
                        .map(({ node }) => {
                            const productGlobalId = node?.merchandise?.product?.id;
                            if (customerProductData.has(productGlobalId)) {
                                const variantIdMatch = node?.merchandise?.id.match(/\d+$/);
                                const variantId = variantIdMatch ? variantIdMatch[0] : null;
                                return {
                                    id: node.id,
                                    quantity: node.quantity,
                                    handle: node.merchandise.product.handle,
                                    product_title: node.merchandise.product.title,
                                    url: `/products/${node.merchandise.product.handle}?variant=${variantId}`,
                                    messagesType: customerProductData.get(productGlobalId),
                                };
                            }
                            return null;
                        })
                        .filter(item => item !== null);
                }
            } catch (error) {
                console.log("Failed to fetch customer cart data:", error.message || error);
            }

            if (cartId && customerCartItems.length) {
                await removeCartItems(cartId, customerCartItems);
            }
        } catch (error) {
            console.error("Error updating cart items:", error.message || error);
        }
    }

    /**
     * Fetches cart data from Shopify Storefront API using the provided cart ID.
     * @param {string} cartId - The global cart ID in the format "gid://shopify/Cart/..."
     * @returns {Promise<Object>} Resolves to the cart data.
     */
    async function getCustomerCartData(cartId) {
        const query = cartDataQuery();
        const variables = { cartId };

        try {
            const response = await fetch(shopifyGraphQlEndpoint, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    "X-Shopify-Storefront-Access-Token": storefrontAccessToken,
                },
                body: JSON.stringify({ query, variables }),
            });
            const data = await response.json();
            if (data.errors) {
                throw new Error(data.errors[0].message || "GraphQL error occurred.");
            }
            return data.data.cart;
        } catch (error) {
            console.error("Error fetching cart data:", error.message || error);
            throw error;
        }
    }

    /**
     * Removes the cart items with the provided line IDs.
     * @param {string} cartId
     * @param {Array} cartItems
     * @returns {Promise<any>}
     */
    async function removeCartItems(cartId, cartItems) {
        const lineIdsToRemove = cartItems.map(item => item.id);
        const query = cartLinesRemoveQuery();
        const variables = { cartId, lineIds: lineIdsToRemove };

        try {
            const response = await fetch(shopifyGraphQlEndpoint, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    "X-Shopify-Storefront-Access-Token": storefrontAccessToken,
                },
                body: JSON.stringify({ query, variables }),
            });
            const responseData = await response.json();

            if (
                responseData.errors ||
                (responseData.data?.cartLinesRemove?.userErrors &&
                    responseData.data.cartLinesRemove.userErrors.length)
            ) {
                const errorMessage =
                    responseData.data?.cartLinesRemove?.userErrors?.[0]?.message ||
                    responseData.errors[0].message;
                if (errorMessage.includes("does not exist")) {
                    console.error("Cart error:", errorMessage);
                }
                throw new Error(errorMessage);
            }

            const removedItems = cartItems.map(item => ({
                handle: item.handle,
                product_title: item.product_title,
                url: item.url,
                messagesType: item.messagesType,
            }));

            if (typeof cartDrawer !== "undefined" && cartDrawer) {
                await cartDrawer.refresh(true);
            } else if (window.location.pathname === theme.routes.cart) {
                const cartItemsEl = document.querySelector("cart-items");
                if (cartItemsEl) {
                    cartItemsEl.dataset.empty === "true" ? window.location.reload() : cartItemsEl.refresh();
                }
            }

            showValidationMessages(removedItems);

            return responseData?.data?.cartLinesRemove?.cart;
        } catch (error) {
            console.error("Error removing items via Storefront API:", error.message || error);
        }
    }

    /**
     * Displays a message for each removed item.
     * @param removedItems
     */
    function showValidationMessages(removedItems) {
        const messagesBlock = document.querySelector('.page-messages__block');
        const closeMessagesBlock = document.querySelector('.page-messages__block .close');
        const targetBlock = document.querySelector('.shopify-section.cc-apps');
        const containers = document.querySelectorAll('.page-messages__block .container .messages');

        if (messagesBlock && targetBlock) {
            targetBlock.insertAdjacentElement('afterend', messagesBlock);
        }

        containers.forEach(container => {
            container.innerHTML = '';

            if (removedItems.length > 1) {
                const groupMessage = document.createElement('p');
                groupMessage.classList.add('group-message');
                groupMessage.innerHTML = `The items below require special processing. Please contact us for further assistance.`;
                container.appendChild(groupMessage);

                const itemList = document.createElement('ul');
                itemList.classList.add('removed-items-list');

                removedItems.forEach(removedItem => {
                    const listItem = document.createElement('li');
                    listItem.classList.add('item-removed');
                    listItem.innerHTML = `<a class="item-link" href="${removedItem.url}" target="_blank">${removedItem.product_title}</a>`;
                    itemList.appendChild(listItem);
                });

                container.appendChild(itemList);
            } else {
                removedItems.forEach(removedItem => {
                    const messageItem = document.createElement('span');
                    messageItem.classList.add('item-removed');
                    messageItem.innerHTML = getMessage(removedItem.messagesType, removedItem.product_title, removedItem.url);
                    container.appendChild(messageItem);
                });
            }
        });

        messagesBlock.removeAttribute('hidden');

        if (closeMessagesBlock) {
            closeMessagesBlock.onclick = function () {
                messagesBlock.setAttribute('hidden', 'hidden');
                containers.forEach(container => {
                    setTimeout(() => {
                        container.innerHTML = '';
                    }, 500);
                });
            };
        }
    }

    /**
     * Displays a message for each removed item.
     * @param messageType
     * @param productTitle
     * @param productUrl
     * @returns {string}
     */
    function getMessage(messageType, productTitle, productUrl) {
        let message = ``;

        if (!productTitle && !messageType) {
            return message;
        }

        switch (messageType) {
            case 'S':
                // Special Quote Item
                message = `This item (<a class="item-link" href="${productUrl}" target="_blank">${productTitle}</a>) requires special handling. Please contact us for further assistance.`;
                break;
            case 'D':
                // Discontinued
                message = `<a class="item-link" href="${productUrl}" target="_blank">${productTitle}</a> has been discontinued and is no longer available for purchase.`;
                break;
            case 'O':
                // Out of Stock
                message = `<a class="item-link" href="${productUrl}" target="_blank">${productTitle}</a> is currently Out of Stock and has been removed from your cart.`;
                break;
            case 'P':
                // Price or Profit
                message = `This item (<a class="item-link" href="${productUrl}" target="_blank">${productTitle}</a>) requires special processing. Please contact us for further assistance.`;
                break;
            default:
                message = `This item (<a class="item-link" href="${productUrl}" target="_blank">${productTitle}</a>) is not available for purchase.`;
                break;
        }
        return message;
    }

    /**
     * Fetches the cart data.
     * @returns {string}
     */
    function cartDataQuery () {
        return `
            query getCartData($cartId: ID!) {
              cart(id: $cartId) {
                id
                lines(first: 250) {
                  edges {
                    node {
                      id
                      quantity
                      merchandise {
                        ... on ProductVariant {
                          id
                          availableForSale
                          product {
                            handle
                            id
                            title
                            vendor
                          }
                        }
                      }
                    }
                  }
                }
              }
            }
      `;
    }

    /**
     * Removes the cart items that match the provided product IDs.
     * @returns {string}
     */
    function cartLinesRemoveQuery() {
        return `
            mutation cartLinesRemove($cartId: ID!, $lineIds: [ID!]!) {
              cartLinesRemove(cartId: $cartId, lineIds: $lineIds) {
                cart {
                  id
                  lines(first: 250) {
                    edges {
                      node {
                        id
                        quantity
                        merchandise {
                          ... on ProductVariant {
                            id
                            product {
                              handle
                            }
                          }
                        }
                      }
                    }
                  }
                }
                userErrors {
                  field
                  message
                }
              }
            }
        `;
    }
});